import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { cn } from '@/app/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-white transition-colors disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-ShengshuButton hover:bg-ShengshuButtonHover text-black font-semibold',
        upscale: 'bg-ShengshuUpscaleButton hover:bg-ShengshuUpscaleButtonHover text-white font-semibold',
        default: 'disabled:text-system-white24',
        solid:
          'text-system-bg02 bg-system-white enabled:hover:bg-[#CACACA] focus-visible:outline-none disabled:opacity-40',
        outline:
          'border border-system-white12 bg-system-white08 text-system-white hover:bg-system-hover01 focus-visible:outline-none disabled:opacity-20 disabled:bg-system-hover01',
        secondary:
          'border border-system-white80 disabled:border-leapX-stroke04 disabled:opacity-40 text-system-text02 [background:radial-gradient(107.5%_107.5%_at_50%_215%,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.00)_100%),rgba(255,255,255,0.01))] hover:bg-leapXBlock',
        text: 'text-system-white disabled:text-system-white24',
        danger:
          'border text-system-error1 border-system-error1 hover:bg-system-error1 hover:text-system-text01 hover:border-system-white12 disabled:opacity-40',
        ghost: 'hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40',
      },
      size: {
        default: 'h-10 px-4 py-2 rounded-8',
        sm: 'h-9 rounded-8 px-3',
        lg: 'h-12 rounded-12 px-8',
        xl: 'h-16 rounded-16 px-9 text-xl sm:h-12 sm:px-6 sm:text-base sm:font-bold sm:rounded-12',
        xxl: 'h-[72px] rounded-16 px-[68px] text-[26px] sm:h-12 sm:px-6 sm:text-base sm:font-bold sm:rounded-12',
        icon: 'h-9 w-9 rounded-8',
        dialog: 'rounded-12 px-6 py-2.5 text-base sm:text-sm',
        original: 'rounded-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  loadingClassName?: string;
  iconClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, icon, children, loading, loadingClassName, iconClassName, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {loading && <Loading className={cn('mr-2 animate-spin text-black', loadingClassName)} />}
        {icon && <div className={cn('shrink-0 text-lg sm:hidden', iconClassName)}>{icon}</div>}
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
